// Detection di indirizzi (IT + varianti internazionali) nel testo delle note/titoli.
// Usato da <LinkifiedText> per rendere gli indirizzi cliccabili → Google Maps.

// Prefissi di "via" comuni in italiano + qualche variante inglese.
// Pattern CI sulla prima lettera (`[Vv]ia` matcha sia "Via" sia "via") perché
// non possiamo usare il flag `i` globale: invaliderebbe `\p{Lu}` nelle parti
// successive della regex (case folding accenderebbe falsi positivi su parole
// minuscole come "ho", "il", ecc. trattate come "città").
const STREET_TYPES = [
  '[Vv]ia', '[Vv]iale', '[Vv]\\.le', '[Cc]orso', '[Cc]\\.so',
  '[Pp]iazza', '[Pp]\\.zza', '[Ll]argo',
  '[Vv]icolo', '[Pp]iazzale', '[Pp]\\.le',
  '[Ss]trada', '[Bb]orgo', '[Ll]ungomare', '[Ll]ungarno',
  '[Ll]ungotevere', '[Rr]ampa', '[Ss]alita',
  '[Gg]alleria', '[Cc]ontrada', '[Tt]raversa',
].join('|')

// Regex: [prefisso] + nome (1-5 parole) + opz numero civico + opz CAP/città + opz sigla.
// Supporta lettere accentate via flag 'u' + \p{L}.
// Nota 1: con flag 'u' gli escape ridondanti (\', \-, \.) dentro character class
// sono ILLEGALI. Uso ' e - nativamente (con - a inizio/fine della class).
// Nota 2: evito lazy quantifier sul nome via (si ferma troppo presto). Uso invece
// un pattern esplicito "parola (spazio parola){0,4}" che gestisce anche nomi
// composti come "Buenos Aires" o "Monte Nero".
// Nota 3: la parte "città" gestisce due varianti per evitare di mangiare testo
// libero successivo (es. "Via Plinio 7, ho ricordato"):
//   a) 1-4 parole che iniziano con MAIUSCOLA → multi-word city ("Reggio Emilia",
//      "San Donato Milanese"). Strong signal, accetta fino a 4 parole.
//   b) 1 parola in qualsiasi caso, ANCORATA a un boundary (fine testo, virgola,
//      punto/!/?, newline) → consente "via montirone 13 vimercate" tutto in
//      minuscolo, ma blocca "via X 19 oggi mi serve" (oggi non è seguito da boundary).
const WORD = `[\\p{L}][\\p{L}0-9'.-]*`
const CITY_UPPER = `[\\p{Lu}][\\p{L}'-]*`    // 1ª parola maiuscola (variante a)
const CITY_CONT  = `[\\p{L}][\\p{L}'-]*`     // parole successive (anche minuscole: "di", "del")
const CITY_LAX   = `[\\p{L}][\\p{L}'-]+`     // 1 parola lunga ≥2 char (variante b)
// Boundary che chiude la città in variante lax: fine stringa, newline, o
// punteggiatura "forte". Lo spazio da solo NON è abbastanza — altrimenti
// la città in variante b mangerebbe tutto come faceva il fallback al precedente.
const CITY_LAX_BOUNDARY = `(?=[,;.!?]|\\s*$|\\n)`
const ADDRESS_RE = new RegExp(
  `\\b(${STREET_TYPES})\\s+` +
  `${WORD}(?:\\s+${WORD}){0,4}` +                                              // nome: 1-5 parole
  `(?:[,\\s]+\\d{1,5}[a-zA-Z]?(?:/\\d+)?)?` +                                  // civico opz (anche dopo virgola: "Via X, 19")
  `(?:[,\\s]+(?:\\d{5}\\s+)?` +
    `(?:` +
      `${CITY_UPPER}(?:\\s+${CITY_CONT}){0,3}` +                               // (a) 1-4 parole, prima maiuscola
      `|${CITY_LAX}${CITY_LAX_BOUNDARY}` +                                     // (b) 1 parola minuscola, ancorata a boundary
    `)` +
  `)?` +
  `(?:\\s+\\([A-Z]{2}\\)|\\s+[A-Z]{2}\\b)?`,                                   // sigla provincia opz: MB o (MB)
  'gu'
)

// URL Google Maps search
export function mapsUrl(address) {
  const q = encodeURIComponent(String(address || '').trim())
  return `https://www.google.com/maps/search/?api=1&query=${q}`
}

// Trova tutti gli indirizzi nel testo. Ritorna array di { raw, start, end }.
export function findAddresses(text) {
  if (!text || typeof text !== 'string') return []
  const matches = []
  // Clona la regex (exec con flag g muta lastIndex)
  const re = new RegExp(ADDRESS_RE.source, ADDRESS_RE.flags)
  let m
  while ((m = re.exec(text)) !== null) {
    // Normalizza: strip spazi finali + eventuale punteggiatura finale
    const raw = m[0].replace(/[\s,.;:]+$/, '')
    matches.push({
      raw,
      start: m.index,
      end: m.index + raw.length,
    })
    if (m.index === re.lastIndex) re.lastIndex++  // safety loop
  }
  return matches
}

// Primo indirizzo trovato (utile per bottone "Apri in Maps" singolo)
export function findFirstAddress(text) {
  const all = findAddresses(text)
  return all[0]?.raw || null
}

// Split del testo in segmenti { text } o { text, address }
// Utile per renderizzare il testo preservando la posizione originale degli indirizzi
export function splitWithAddresses(text) {
  if (!text) return []
  const addrs = findAddresses(text)
  if (!addrs.length) return [{ text }]
  const segments = []
  let last = 0
  for (const a of addrs) {
    if (a.start > last) segments.push({ text: text.slice(last, a.start) })
    segments.push({ text: a.raw, address: a.raw })
    last = a.end
  }
  if (last < text.length) segments.push({ text: text.slice(last) })
  return segments
}
